// src/app/components/employee-login/employee-login.component.ts
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import {
  Firestore,
  collection,
  collectionData,
  query,
  where,
  getDocs,
  DocumentData,
} from '@angular/fire/firestore';
import { AuthService } from 'src/app/auth.service';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import {
  map,
  takeUntil,
  filter,
  switchMap,
  tap,
  catchError,
} from 'rxjs/operators';
import { of } from 'rxjs';

interface Organization {
  id: string;
  name: string;
}

interface Employee {
  id: string;
  Name: string;
  Pin: string;
  orgId: string;
  email?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-employee-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './employee-login.component.html',
  styleUrls: ['./employee-login.component.css'],
})
export class EmployeeLoginComponent implements OnInit, OnDestroy {
  private readonly firestore = inject(Firestore);
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  // Login method toggle - Default to PIN
  loginMethod: 'pin' | 'email' = 'pin';

  // PIN Login Form
  readonly pinForm = this.fb.nonNullable.group({
    orgId: ['', Validators.required],
    employeeId: ['', Validators.required],
    pin: ['', [Validators.required, Validators.pattern(/^\d{4,6}$/)]],
  });

  // Email Login Form
  readonly emailForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(6)]],
  });

  orgs$!: Observable<Organization[]>;
  employees: Employee[] = [];
  isLoadingEmployees = false;
  isLoading = false;
  showPin = false;
  showPassword = false;

  toastMessage = '';
  toastType: 'success' | 'error' = 'success';
  showToast = false;

  ngOnInit(): void {
    this.initializeOrganizations();
    this.setupOrgChangeHandler();
    this.setupEmployeeChangeHandler();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeOrganizations(): void {
    const orgRef = collection(this.firestore, 'organizations');
    this.orgs$ = collectionData(orgRef, { idField: 'id' }).pipe(
      map((orgs) =>
        (orgs as Organization[]).sort((a, b) => a.name.localeCompare(b.name))
      ),
      catchError((error) => {
        console.error('Error loading organizations:', error);
        this.showCustomToast('Failed to load stores', 'error');
        return of([]);
      })
    );
  }

  private setupOrgChangeHandler(): void {
    this.pinForm.controls.orgId.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter((orgId) => !!orgId),
        tap(() => {
          this.pinForm.patchValue(
            { employeeId: '', pin: '' },
            { emitEvent: false }
          );
          this.employees = [];
          this.isLoadingEmployees = true;
        }),
        switchMap((orgId) => this.loadEmployees(orgId))
      )
      .subscribe({
        next: (employees) => {
          this.employees = employees;
          this.isLoadingEmployees = false;

          if (employees.length === 0) {
            this.showCustomToast('No employees found for this store', 'error');
          }
        },
        error: (error) => {
          console.error('Error loading employees:', error);
          this.showCustomToast('Failed to load employees', 'error');
          this.isLoadingEmployees = false;
        },
      });
  }

  private setupEmployeeChangeHandler(): void {
    this.pinForm.controls.employeeId.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter((employeeId) => !!employeeId)
      )
      .subscribe(() => {
        this.pinForm.patchValue({ pin: '' }, { emitEvent: false });
      });
  }

  private async loadEmployees(orgId: string): Promise<Employee[]> {
    try {
      const empRef = collection(this.firestore, 'Employees');
      const q = query(empRef, where('orgId', '==', orgId));
      const snapshot = await getDocs(q);

      return snapshot.docs
        .map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            } as Employee)
        )
        .sort((a, b) => a.Name.localeCompare(b.Name));
    } catch (error) {
      console.error('Error in loadEmployees:', error);
      throw error;
    }
  }

  get showEmployeeDropdown(): boolean {
    return (
      !!this.pinForm.value.orgId &&
      !this.isLoadingEmployees &&
      this.employees.length > 0
    );
  }

  get showPinField(): boolean {
    return !!this.pinForm.value.employeeId;
  }

  /**
   * Switch between PIN and Email login methods
   */
  switchLoginMethod(method: 'pin' | 'email'): void {
    this.loginMethod = method;

    // Reset both forms
    this.pinForm.reset();
    this.emailForm.reset();

    // Clear data
    this.employees = [];
    this.showPin = false;
    this.showPassword = false;

    // console.log(`Switched to ${method} login`);
  }

  /**
   * PIN Login - Uses AuthService.loginWithPin()
   * FIXED: Now passes employeeId to validate PIN against specific employee
   */
  async onPinSubmit(): Promise<void> {
    if (this.pinForm.invalid || this.isLoading) {
      this.pinForm.markAllAsTouched();
      return;
    }

    const { orgId, employeeId, pin } = this.pinForm.getRawValue();
    this.isLoading = true;

    try {
      // ✅ Pass employeeId to ensure PIN is validated against the selected employee
      const user = await this.auth.loginWithPin(pin, orgId, employeeId);

      this.showCustomToast('✅ Login successful!', 'success');

      setTimeout(() => {
        this.navigateBasedOnRole(user);
      }, 500);
    } catch (error: any) {
      console.error('PIN login error:', error);
      this.showCustomToast(
        error.message || '❌ Incorrect PIN. Please try again.',
        'error'
      );
      this.pinForm.patchValue({ pin: '' });
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Email Login - Uses AuthService.login() with bcrypt
   * Requires: Email and Password only
   */
  async onEmailSubmit(): Promise<void> {
    if (this.emailForm.invalid || this.isLoading) {
      this.emailForm.markAllAsTouched();
      return;
    }

    const { email, password } = this.emailForm.getRawValue();
    this.isLoading = true;

    try {
      const user = await this.auth.login(email, password);

      this.showCustomToast('✅ Login successful!', 'success');

      setTimeout(() => {
        this.navigateBasedOnRole(user);
      }, 500);
    } catch (error: any) {
      console.error('Email login error:', error);
      this.showCustomToast(
        error.message || '❌ Invalid email or password.',
        'error'
      );
      this.emailForm.patchValue({ password: '' });
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Navigate user based on their role
   */
  private navigateBasedOnRole(user: any): void {
    const role = user?.role?.toLowerCase?.() || 'user';

    if (role === 'admin') {
      this.router.navigate(['/admin']);
    } else if (role === 'manager') {
      this.router.navigate(['/manager']);
    } else {
      this.router.navigate(['/public-schedule']);
    }
  }

  showCustomToast(msg: string, type: 'success' | 'error'): void {
    this.toastMessage = msg;
    this.toastType = type;
    this.showToast = true;
    setTimeout(() => (this.showToast = false), 4000);
  }

  closeToast(): void {
    this.showToast = false;
  }

  togglePinVisibility(): void {
    this.showPin = !this.showPin;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}